/**
 * ShareCardRenderer Component
 *
 * Off-screen renderer for share cards using react-native-view-shot.
 * Captures share card components as JPEG images at fixed 1080x1920px resolution.
 *
 * ## Key Features
 * - **Off-screen rendering**: Hidden from user view (position: absolute, top: -10000)
 * - **Fixed dimensions**: Always renders at 1080×1920px (Instagram Stories)
 * - **Image capture**: Uses ViewShot to generate high-quality JPEG (90% quality)
 * - **Error handling**: Returns null on capture failure with console warning
 *
 * ## Workflow
 * ```
 * 1. Parent sets cardData (triggers off-screen render)
 * 2. ShareCardRenderer mounts card component off-screen
 * 3. captureImage() called → ViewShot.captureRef()
 * 4. Returns URI string → parent shares via expo-sharing
 * 5. Parent clears cardData (unmounts off-screen component)
 * ```
 *
 * ## Usage
 * ```tsx
 * const rendererRef = useRef<ShareCardRendererRef>(null);
 * const [cardData, setCardData] = useState(null);
 *
 * const handleShare = async () => {
 *   setCardData({ type: 'narrator', episodeTitle: '...', ... });
 *   await new Promise(resolve => setTimeout(resolve, 100)); // Wait for render
 *   const uri = await rendererRef.current?.captureImage();
 *   if (uri) await Sharing.shareAsync(uri);
 *   setCardData(null); // Cleanup
 * };
 *
 * return (
 *   <>
 *     <Button title="Share" onPress={handleShare} />
 *     {cardData && <ShareCardRenderer ref={rendererRef} cardData={cardData} />}
 *   </>
 * );
 * ```
 *
 * Created: 2025-01-11 (Task 2.2: Viral Share Formats)
 */

import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { View, StyleSheet } from 'react-native';
import ViewShot, { type ViewShotRef } from 'react-native-view-shot';
import { ShareCardTemplate } from './ShareCardTemplate';
import { NarratorShareCard } from './NarratorShareCard';
import { ExpertShareCard } from './ExpertShareCard';
import { CombinedShareCard } from './CombinedShareCard';
import type { MessageContext } from '@dramadetector/shared';
import type { ShareFormat } from '@dramadetector/shared/og';

/**
 * Card data for Narrator-style share
 */
export interface NarratorCardData {
  type: 'narrator';
  episodeTitle: string;
  dramaScore: number;
  context?: MessageContext;
}

/**
 * Card data for Drama Expert-style share
 */
export interface ExpertCardData {
  type: 'expert';
  insightText: string;
  patternTag: string;
  dramaScore: number;
}

/**
 * Card data for Combined (dual-voice) share
 */
export interface CombinedCardData {
  type: 'combined';
  episodeTitle: string;
  dramaScore: number;
  insightText: string;
  patternTag: string;
}

/**
 * Union type for all card data variants
 */
export type ShareCardData = NarratorCardData | ExpertCardData | CombinedCardData;

/**
 * Props for ShareCardRenderer component
 */
export interface ShareCardRendererProps {
  /**
   * Card data to render (determines card type and content)
   */
  cardData: ShareCardData;

  /**
   * Output format for the card. Determines dimensions.
   * @default 'reel'
   */
  format?: ShareFormat;

  /**
   * Optional callback fired when component is fully rendered
   * Used to ensure render completion before image capture
   */
  onRendered?: () => void;

  /**
   * Optional test ID for E2E testing
   * @default 'share-card-renderer'
   */
  testID?: string;
}

/**
 * Ref methods exposed by ShareCardRenderer
 */
export interface ShareCardRendererRef {
  /**
   * Capture the rendered card as a JPEG image
   * @returns Promise resolving to image URI (file:// path) or null on error
   */
  captureImage: () => Promise<string | null>;
}

export const ShareCardRenderer = forwardRef<
  ShareCardRendererRef,
  ShareCardRendererProps
>(({ cardData, format = 'reel', onRendered, testID = 'share-card-renderer' }, ref) => {
  const viewShotRef = useRef<ViewShotRef>(null);

  // Expose captureImage method to parent via ref
  useImperativeHandle(ref, () => ({
    captureImage: async () => {
      try {
        if (!viewShotRef.current) {
          console.warn('[ShareCardRenderer] ViewShot ref is null');
          return null;
        }

        if (!viewShotRef.current.capture) {
          console.warn('[ShareCardRenderer] ViewShot capture method unavailable');
          return null;
        }

        // Capture as JPEG at 90% quality for optimal file size
        const uri = await viewShotRef.current.capture();
        return uri;
      } catch (error) {
        console.error('[ShareCardRenderer] Image capture failed:', error);
        return null;
      }
    },
  }));

  // Render appropriate card based on type
  const renderCard = () => {
    switch (cardData.type) {
      case 'narrator':
        return (
          <ShareCardTemplate backgroundColor="#F5F3FF" format={format}>
            <NarratorShareCard
              episodeTitle={cardData.episodeTitle}
              dramaScore={cardData.dramaScore}
              context={cardData.context}
              testID={`${testID}-narrator-card`}
            />
          </ShareCardTemplate>
        );

      case 'expert':
        return (
          <ShareCardTemplate backgroundColor="#EFF6FF" format={format}>
            <ExpertShareCard
              insightText={cardData.insightText}
              patternTag={cardData.patternTag}
              dramaScore={cardData.dramaScore}
              testID={`${testID}-expert-card`}
            />
          </ShareCardTemplate>
        );

      case 'combined':
        return (
          <ShareCardTemplate backgroundColor="#FFFFFF" format={format}>
            <CombinedShareCard
              episodeTitle={cardData.episodeTitle}
              dramaScore={cardData.dramaScore}
              insightText={cardData.insightText}
              patternTag={cardData.patternTag}
              testID={`${testID}-combined-card`}
            />
          </ShareCardTemplate>
        );

      default:
        console.warn('[ShareCardRenderer] Unknown card type:', cardData);
        return null;
    }
  };

  return (
    <View
      style={styles.offscreenContainer}
      testID={testID}
      onLayout={onRendered} // Fire callback when layout is complete
    >
      <ViewShot
        ref={viewShotRef}
        options={{
          format: 'jpg',
          quality: 0.9,
          result: 'tmpfile', // Save to temp file (returns file:// URI)
        }}
      >
        {renderCard()}
      </ViewShot>
    </View>
  );
});

ShareCardRenderer.displayName = 'ShareCardRenderer';

const styles = StyleSheet.create({
  offscreenContainer: {
    position: 'absolute',
    top: -10000, // Render off-screen (hidden from user)
    left: -10000,
    // CRITICAL: Do NOT add width/height constraints here
    // Let ShareCardTemplate control dimensions (1080×1920)
  },
});
